// VERSION 2.2 - FINAL WORKFLOW FIX
// =============================================================================
//  SCREEN: TournamentScreen (Mit Konfiguration für KO-Spielmodi)
// =============================================================================
import React, { useState, useEffect } from 'react';
import apiClient from '../api';
import { CLASSIFICATIONS } from '../constants/classificationConstants';
import { GAME_MODES } from '../constants/gameModeConstants';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const TournamentList = ({ tournaments, onEdit, onDelete }) => (
  <div className="table-container">
    <h3>Bestehende Turniere</h3>
    <table>
      <thead><tr><th>Name</th><th>Zeitraum</th><th>Ort</th><th>Aktionen</th></tr></thead>
      <tbody>
        {tournaments.length > 0 ? (
          tournaments.map((t) => (
            <tr key={t._id}>
              <td><Link to={`/turnier/${t._id}`}>{t.name}</Link></td>
              <td>{new Date(t.startDate).toLocaleDateString('de-DE', { timeZone: 'UTC' })} - {new Date(t.endDate).toLocaleDateString('de-DE', { timeZone: 'UTC' })}</td>
              <td>{t.location}</td>
              <td>
                <button className="btn-edit" onClick={() => onEdit(t)}>Bearbeiten</button>
                <button className="btn-delete" onClick={() => onDelete(t._id)}>Löschen</button>
              </td>
            </tr>
          ))
        ) : (
          <tr><td colSpan="4">Noch keine Turniere angelegt.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const TournamentScreen = () => {
  const [tournaments, setTournaments] = useState([]);
  const [editingTournament, setEditingTournament] = useState(null);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [selectedClassifications, setSelectedClassifications] = useState([]);
  const [selectedTeamClassifications, setSelectedTeamClassifications] = useState([]);
  const [selectedGameModes, setSelectedGameModes] = useState([]);
  const [koConfiguration, setKoConfiguration] = useState({});
  const [numberOfLanes, setNumberOfLanes] = useState(4);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);

  useEffect(() => { fetchTournaments(); }, []);

  const fetchTournaments = async () => {
    try { const { data } = await apiClient.get('/api/tournaments'); setTournaments(data); } 
    catch (err) { toast.error('Fehler beim Laden der Turniere.'); }
  };

  const resetForm = () => {
    setEditingTournament(null); setName(''); setStartDate(''); setEndDate(''); setLocation('');
    setSelectedClassifications([]); setSelectedTeamClassifications([]); setSelectedGameModes([]);
    setKoConfiguration({}); setNumberOfLanes(4); setScheduleBlocks([]);
  };

  const handleClassificationChange = (key, isTeamClassification) => {
    const stateSetter = isTeamClassification ? setSelectedTeamClassifications : setSelectedClassifications;
    stateSetter(current => current.includes(key) ? current.filter(c => c !== key) : [...current, key]);
  };

  const handleGameModeChange = (key) => { setSelectedGameModes(current => current.includes(key) ? current.filter(m => m !== key) : [...current, key]); };
  
  const handleKoConfigChange = (gameModeKey, field, value) => {
    setKoConfiguration(prev => ({ ...prev, [gameModeKey]: { ...prev[gameModeKey], [field]: value } }));
  };
  
  const addScheduleBlock = () => { setScheduleBlocks([...scheduleBlocks, { date: '', startTime: '16:00', endTime: '21:00', interval: 60 }]); };
  const handleBlockChange = (index, field, value) => { const newBlocks = [...scheduleBlocks]; newBlocks[index][field] = value; setScheduleBlocks(newBlocks); };
  const removeScheduleBlock = (index) => { setScheduleBlocks(scheduleBlocks.filter((_, i) => i !== index)); };

  const handleEdit = (t) => {
    setEditingTournament(t);
    setName(t.name);
    setStartDate(t.startDate.substring(0, 10));
    setEndDate(t.endDate.substring(0, 10));
    setLocation(t.location || '');
    setSelectedClassifications(t.classifications);
    setSelectedTeamClassifications(t.teamClassifications || []);
    setSelectedGameModes(t.gameModes || ['CLASSIC_120']);
    const initialKoConfig = {};
    (t.koConfiguration || []).forEach(config => {
      initialKoConfig[config.gameMode] = { enabled: true, bracketSize: config.bracketSize };
    });
    setKoConfiguration(initialKoConfig);
    setNumberOfLanes(t.numberOfLanes || 4);
    setScheduleBlocks((t.scheduleBlocks || []).map(b => ({ ...b, date: b.date.substring(0, 10) })));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Dieses Turnier wirklich löschen? Alle zugehörigen Daten gehen verloren!')) {
      try { await apiClient.delete(`/api/tournaments/${id}`); toast.success('Turnier erfolgreich gelöscht.'); fetchTournaments(); } 
      catch (err) { toast.error('Fehler beim Löschen.'); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !startDate || !endDate || selectedClassifications.length === 0 || selectedGameModes.length === 0) {
      toast.error('Name, Daten, mind. eine Einzel-Wertung und mind. ein Spielmodus sind Pflichtfelder.');
      return;
    }
    const koConfigForBackend = Object.entries(koConfiguration)
      .filter(([key, value]) => value.enabled && selectedGameModes.includes(key))
      .map(([key, value]) => ({ gameMode: key, bracketSize: parseInt(value.bracketSize, 10) || 16 }));

    const tournamentData = { name, startDate, endDate, location, classifications: selectedClassifications, teamClassifications: selectedTeamClassifications, gameModes: selectedGameModes, koConfiguration: koConfigForBackend, numberOfLanes, scheduleBlocks };

    try {
      if (editingTournament) { await apiClient.put(`/api/tournaments/${editingTournament._id}`, tournamentData); toast.success('Turnier erfolgreich aktualisiert.'); } 
      else { await apiClient.post('/api/tournaments', tournamentData); toast.success('Turnier erfolgreich angelegt.'); }
      resetForm();
      fetchTournaments();
    } catch (err) { toast.error(err.response?.data?.message || 'Ein Fehler ist aufgetreten.'); }
  };

  const singleClassifications = CLASSIFICATIONS.filter(c => !c.key.startsWith('team_'));
  const teamClassifications = CLASSIFICATIONS.filter(c => c.key.startsWith('team_'));

  return (
    <div className="screen-container">
      <h2>Turnierverwaltung</h2>
      <div className="form-container">
        <h3>{editingTournament ? 'Turnier bearbeiten' : 'Neues Turnier anlegen'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group"><label>Turniername*</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="form-row"><div className="form-group"><label>Startdatum*</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div><div className="form-group"><label>Enddatum*</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div></div>
          <div className="form-group"><label>Austragungsort</label><input type="text" value={location} onChange={(e) => setLocation(e.target.value)} /></div>
          <div className="form-group">
            <label>Angebotene Spielmodi / Disziplinen*</label>
            <div className="checkbox-group">
              {GAME_MODES.map(mode => (
                <div key={mode.key} className="checkbox-item" style={{display: 'block', marginBottom: '1rem'}}>
                  <div>
                    <input type="checkbox" id={mode.key} value={mode.key} checked={selectedGameModes.includes(mode.key)} onChange={() => handleGameModeChange(mode.key)} />
                    <label htmlFor={mode.key}>{mode.label}</label>
                  </div>
                  {selectedGameModes.includes(mode.key) && ['SPRINT', 'TANDEM', 'PAARBEWERB'].includes(mode.key) && (
                    <div style={{marginLeft: '2rem', marginTop: '0.5rem'}}>
                      <input type="checkbox" id={`ko-${mode.key}`} checked={koConfiguration[mode.key]?.enabled || false} onChange={(e) => handleKoConfigChange(mode.key, 'enabled', e.target.checked)} />
                      <label htmlFor={`ko-${mode.key}`} style={{fontWeight: 'normal'}}>KO-Phase aktivieren</label>
                      {koConfiguration[mode.key]?.enabled && (
                        <select value={koConfiguration[mode.key]?.bracketSize || 16} onChange={(e) => handleKoConfigChange(mode.key, 'bracketSize', e.target.value)} style={{marginLeft: '1rem', padding: '0.2rem', fontSize: '0.9rem'}}>
                          <option value="8">8er Raster</option><option value="16">16er Raster</option><option value="32">32er Raster</option>
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="form-group"><label>Angebotene Einzel-Wertungen*</label><div className="checkbox-group">{singleClassifications.map(c => (<div key={c.key} className="checkbox-item"><input type="checkbox" id={c.key} value={c.key} checked={selectedClassifications.includes(c.key)} onChange={() => handleClassificationChange(c.key, false)} /><label htmlFor={c.key}>{c.label}</label></div>))}</div></div>
          <div className="form-group"><label>Angebotene Mannschafts-Wertungen</label><div className="checkbox-group">{teamClassifications.map(c => (<div key={c.key} className="checkbox-item"><input type="checkbox" id={c.key} value={c.key} checked={selectedTeamClassifications.includes(c.key)} onChange={() => handleClassificationChange(c.key, true)} /><label htmlFor={c.key}>{c.label}</label></div>))}</div></div>
          <hr style={{ margin: '2rem 0' }} />
          <h4>Startplan-Konfiguration</h4>
          <div className="form-group"><label>Anzahl der Bahnen</label><input type="number" min="1" value={numberOfLanes} onChange={(e) => setNumberOfLanes(parseInt(e.target.value, 10))} /></div>
          <div className="form-group"><label>Spieltage und Zeiten</label>
            {scheduleBlocks.map((block, index) => (<div key={index} className="schedule-block" style={{display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.5rem'}}><input type="date" value={block.date} onChange={e => handleBlockChange(index, 'date', e.target.value)} /> von <input type="time" value={block.startTime} onChange={e => handleBlockChange(index, 'startTime', e.target.value)} /> bis <input type="time" value={block.endTime} onChange={e => handleBlockChange(index, 'endTime', e.target.value)} /> alle <input type="number" value={block.interval} onChange={e => handleBlockChange(index, 'interval', parseInt(e.target.value, 10))} min="15" step="15" style={{ width: '60px' }} /> Min. <button type="button" className="btn-delete-small" onClick={() => removeScheduleBlock(index)}>X</button></div>))}
            <button type="button" onClick={addScheduleBlock} style={{alignSelf: 'flex-start'}}>+ Spieltag hinzufügen</button>
          </div>
          <div className="form-buttons"><button type="submit">{editingTournament ? 'Änderungen speichern' : 'Turnier anlegen'}</button>{editingTournament && <button type="button" className="btn-cancel" onClick={resetForm}>Abbrechen</button>}</div>
        </form>
      </div>
      <hr />
      <TournamentList tournaments={tournaments} onEdit={handleEdit} onDelete={handleDelete} />
    </div>
  );
};
export default TournamentScreen;